import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  Group,
  Button,
  Badge,
  Text,
  Paper,
  Stack,
  Skeleton,
  Pagination,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import { IconArrowLeft, IconDownload, IconFileCode } from '@tabler/icons-react';
import { customersService } from '../../services/api/customers';
import { reportsService } from '../../services/api/reports';
import { SearchToolbar } from '../../components/shared';
import type { CustomerReport, ReportStatus } from '../../types';
import dayjs from 'dayjs';

const PAGE_SIZE = 20;

const STATUS_COLORS: Record<ReportStatus, string> = {
  pending: 'yellow',
  generated: 'green',
  failed: 'red',
};

export function CustomerReportsPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customersService.getById(customerId!),
    enabled: !!customerId,
  });

  const { data: reportsData, isLoading } = useQuery({
    queryKey: ['customer-reports', customerId],
    queryFn: () => reportsService.getByCustomerId(customerId!, { limit: 1000 }),
    enabled: !!customerId,
  });

  const handleDownloadPdf = useCallback(
    async (reportId: string) => {
      if (!customerId) return;
      const blob = await reportsService.downloadPdf(customerId, reportId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay to allow the browser to load the PDF
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    },
    [customerId]
  );

  const handleDownloadData = useCallback(
    async (reportId: string) => {
      if (!customerId) return;
      const blob = await reportsService.downloadData(customerId, reportId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}-data.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    [customerId]
  );

  const reports = reportsData?.reports || [];

  const filteredReports = search
    ? reports.filter(
        (r: CustomerReport) =>
          r.licenseEid.toLowerCase().includes(search.toLowerCase()) ||
          r.status.toLowerCase().includes(search.toLowerCase()) ||
          r.id.toLowerCase().includes(search.toLowerCase())
      )
    : reports;

  const totalPages = Math.ceil(filteredReports.length / PAGE_SIZE);
  const paginatedReports = filteredReports.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/customers')}
          >
            Back
          </Button>
          <Title order={2}>
            Reports{customer ? ` - ${customer.name}` : ''}
          </Title>
        </Group>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v: string) => {
          setSearch(v);
          setPage(1);
        }}
        placeholder="Search by EID, status, or report ID..."
      />

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : filteredReports.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No reports found
          </Text>
        ) : (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Report ID</Table.Th>
                  <Table.Th>License EID</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Generated At</Table.Th>
                  <Table.Th>Created At</Table.Th>
                  <Table.Th w={100}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginatedReports.map((report: CustomerReport) => (
                  <Table.Tr key={report.id}>
                    <Table.Td>
                      <Tooltip label={report.id}>
                        <Text size="sm" ff="monospace">
                          {report.id.slice(0, 8)}...
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {report.licenseEid}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={STATUS_COLORS[report.status]}
                        variant="light"
                      >
                        {report.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {report.pdfGeneratedAt
                          ? dayjs(report.pdfGeneratedAt).format(
                              'MMM D, YYYY h:mm A'
                            )
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {dayjs(report.createdAt).format('MMM D, YYYY h:mm A')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {report.status === 'generated' && (
                          <Tooltip label="View PDF">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => handleDownloadPdf(report.id)}
                            >
                              <IconDownload size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {/* Raw data exists for every report (stored at
                            creation), so it stays available even when PDF
                            generation failed */}
                        <Tooltip label="Download Data (JSON)">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleDownloadData(report.id)}
                          >
                            <IconFileCode size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group
              justify="space-between"
              px="md"
              py="sm"
              style={{
                borderTop: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <Text size="sm" c="dimmed">
                Showing{' '}
                {Math.min(
                  (page - 1) * PAGE_SIZE + 1,
                  filteredReports.length
                )}
                –{Math.min(page * PAGE_SIZE, filteredReports.length)} of{' '}
                {filteredReports.length}
              </Text>
              {totalPages > 1 && (
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              )}
            </Group>
          </>
        )}
      </Paper>
    </Container>
  );
}
